import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { SUBMIT_EMPLOYMENT } from 'graphql/resolvers/admin/candidates/mutations';
import {
  EMPLOYMENT_SUBMISSION,
  GET_CANDIDATE_VERIFICATION_REQUEST
} from 'graphql/resolvers/client/queries';
import { employmentVerificationValidationSchema } from 'helpers/validators';
import { toastSuccess, toastError } from 'components/common/Toast';
import Loader from 'components/common/Loader';
import { svgs } from 'assets';
import EmploymentReport from 'components/VerificationReports/EmploymentReport';

// --- Warm, guided write-form palette & styles ---
// This is the form a referee (a stranger, often on a phone) fills out to
// verify a candidate's employment history. Distinct from the internal
// report: friendlier, reassuring, one clear question at a time.
const form = {
  ink: '#22303f',
  slate: '#6b7d8f',
  paper: '#faf7f0',
  cream: '#fffdf9',
  line: '#e7e0d0',
  teal: '#408996',
  tealDeep: '#2f6771',
  tealTint: 'rgba(64, 137, 150, 0.08)',
  amber: '#c8842d',
  blue: '#007bff',
  blueDeep: '#0062cc',
  blueTint: 'rgba(0, 123, 255, 0.08)'
};

const formStyles = {
  wrapper: {
    background: form.paper,
    minHeight: '100vh',
    padding: '32px 16px 90px'
  },
  shell: { maxWidth: '760px', margin: '0 auto' },
  formCard: {
    background: form.cream,
    borderRadius: '12px',
    border: `1px solid ${form.line}`,
    boxShadow: '0 1px 2px rgba(20, 30, 40, 0.04)',
    overflow: 'hidden'
  },
  headerCard: {
    background: form.blueTint,
    borderBottom: `1px solid rgba(0, 123, 255, 0.18)`,
    padding: '30px 30px 26px'
  },
  brandBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '22px'
  },
  brandLogo: { width: '52px', height: '52px', flexShrink: 0 },
  brandName: {
    fontSize: '1.3rem',
    fontWeight: '700',
    color: form.ink,
    letterSpacing: '0.2px'
  },
  intro: { textAlign: 'center' },
  eyebrow: {
    color: form.tealDeep,
    fontSize: '1.4rem',
    fontWeight: '700',
    letterSpacing: '0.5px',
    marginBottom: '12px'
  },
  subtitle: {
    color: form.ink,
    fontSize: '1.2rem',
    lineHeight: '1.55',
    maxWidth: '560px',
    margin: '0 auto'
  },
  progressTrack: {
    display: 'flex',
    gap: '5px',
    marginTop: '20px'
  },
  progressSegment: (filled) => ({
    flex: 1,
    height: '4px',
    borderRadius: '999px',
    background: filled ? form.teal : form.line
  }),
  progressLabel: {
    color: form.slate,
    fontSize: '1rem',
    fontWeight: '600',
    marginTop: '10px',
    letterSpacing: '0.3px'
  },
  card: {
    padding: '24px 0',
    borderBottom: `1px solid ${form.line}`
  },
  cardLast: {
    padding: '24px 0'
  },
  cardHead: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    marginBottom: '18px'
  },
  cardIndex: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: form.tealTint,
    color: form.teal,
    fontWeight: '700',
    fontSize: '0.98rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  question: {
    fontSize: '1.15rem',
    fontWeight: '600',
    color: form.ink,
    lineHeight: '1.45'
  },
  helperText: {
    fontSize: '0.98rem',
    color: form.amber,
    fontWeight: '500',
    marginTop: '4px'
  },
  fieldLabel: {
    width: '90%',
    margin: '0 auto 8px',
    fontSize: '1rem',
    fontWeight: '600',
    color: form.slate,
    display: 'block'
  },
  required: { color: '#c0392b', marginLeft: '3px' },
  input: {
    width: '90%',
    margin: '0 auto',
    display: 'block',
    padding: '13px 14px',
    borderRadius: '8px',
    border: `1.5px solid ${form.line}`,
    fontSize: '1.16rem',
    color: form.ink,
    background: '#fff'
  },
  choiceGrid: (columns = 'auto-fit') => ({
    display: 'grid',
    gridTemplateColumns:
      columns === 'auto-fit' ? 'repeat(auto-fit, minmax(150px, 1fr))' : columns,
    gap: '10px',
    width: '90%',
    margin: '0 auto'
  }),
  choice: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '13px 16px',
    borderRadius: '8px',
    border: `1.5px solid ${selected ? form.blue : form.line}`,
    background: selected ? form.blue : '#fff',
    color: selected ? '#fff' : form.ink,
    fontWeight: selected ? '600' : '500',
    fontSize: '1.08rem',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background 0.15s ease'
  }),
  commentLabel: {
    width: '90%',
    fontSize: '1rem',
    fontWeight: '600',
    color: form.slate,
    margin: '16px auto 8px',
    display: 'block'
  },
  errorText: {
    width: '90%',
    margin: '8px auto 0',
    color: '#c0392b',
    fontSize: '1rem',
    fontWeight: '500'
  },
  matrixWrapper: { width: '90%', margin: '0 auto', overflowX: 'auto' },
  matrixTable: { borderCollapse: 'collapse', width: '100%', minWidth: '560px' },
  matrixHeader: {
    background: form.tealTint,
    fontWeight: '700',
    color: form.ink,
    textAlign: 'center',
    padding: '12px',
    fontSize: '0.95rem'
  },
  matrixCell: {
    padding: '12px',
    border: `1px solid ${form.line}`,
    textAlign: 'center'
  },
  agreementCard: {
    background: form.blueTint,
    border: `1px solid rgba(0, 123, 255, 0.25)`,
    borderRadius: '10px',
    padding: '20px 22px',
    marginBottom: '20px',
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start'
  },
  submitBar: { display: 'flex', justifyContent: 'flex-end' },
  submitButton: {
    padding: '15px 34px',
    borderRadius: '9px',
    border: 'none',
    background: form.blue,
    color: '#fff',
    fontWeight: '700',
    fontSize: '1.18rem',
    letterSpacing: '0.3px',
    cursor: 'pointer',
    boxShadow: '0 10px 22px -8px rgba(0, 98, 204, 0.45)'
  }
};

const ratingScale = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];
const ratingParameters = [
  { id: 'honesty', label: 'Honesty' },
  { id: 'reliability', label: 'Reliability' },
  { id: 'teamSpirit', label: 'Team Spirit' },
  { id: 'decisiveness', label: 'Decisiveness' },
  { id: 'effectiveness', label: 'Effectiveness' },
  { id: 'punctuality', label: 'Punctuality' }
];

const EmploymentVerificationForm = ({
  match: { params },
  employeeName: defaultEmployeeName = 'The Candidate'
}) => {
  const requestId = params?.id;
  const verificationId = params?.verificationId;

  const [showLoader, setLoaderState] = useState(false);
  const [submitEmploymentForm] = useMutation(SUBMIT_EMPLOYMENT);

  const { data: submissionData, refetch: refetchSubmission } = useQuery(
    EMPLOYMENT_SUBMISSION,
    {
      variables: {
        employmentId: requestId,
        verificationId: verificationId
      },
      skip: !requestId
    }
  );

  const { data: candidateData } = useQuery(GET_CANDIDATE_VERIFICATION_REQUEST, {
    variables: { id: verificationId },
    skip: !verificationId
  });

  const candidate = candidateData?.verificationRequest?.candidate;
  const candidateName = candidate
    ? `${candidate.firstname || ''} ${candidate.lastname || ''}`.trim()
    : '';
  const employeeName = candidateName || defaultEmployeeName;

  const isReadOnly = !!submissionData?.employmentSubmission;

  const parsedExtraData = submissionData?.employmentSubmission?.formData
    ? JSON.parse(submissionData?.employmentSubmission?.formData)
    : {};

  const options = {
    natureOfEmployment: ['NYSC', 'Contract', 'Permanent'],
    reasonForLeaving: ['Termination', 'Dismissal', 'Resignation']
  };

  const handleOnSubmit = async (values) => {
    setLoaderState(true);
    try {
      const response = await submitEmploymentForm({
        variables: {
          input: {
            requestId: requestId,
            fullname: values.refereeName,
            email: values.email,
            phone: values.phone,
            address: values.refereeAddress,
            idCard: values.idCard,
            formData: JSON.stringify({
              natureOfEmployment: values.natureOfEmployment,
              durationFrom: values.durationFrom,
              durationTo: values.durationTo,
              lastGradeLevel: values.lastGradeLevel,
              deptPositionA: values.deptPositionA,
              deptPositionB: values.deptPositionB,
              deptPositionC: values.deptPositionC,
              totalGrossSalary: values.totalGrossSalary,
              nonMonetizedBenefits: values.nonMonetizedBenefits,
              reasonForLeaving: values.reasonForLeaving,
              reasonForLeavingDetails: values.reasonForLeavingDetails,
              integrityOpinion: values.integrityOpinion,
              ratings: values.ratings,
              refereeJobTitle: values.refereeJobTitle,
              refereeCompany: values.refereeCompany
            })
          }
        }
      });

      if (response.data?.submitEmploymentForm?.ok) {
        toastSuccess(
          svgs.SUCCESS_ICON,
          'Employment Verification Form Submitted Successfully'
        );
        if (refetchSubmission) {
          await refetchSubmission();
        }
      } else {
        const errors = response.data?.submitEmploymentForm?.errors?.messages.join(', ');
        toastError(svgs.DELETE_ICON, errors || 'Submission failed.');
      }
    } catch (e) {
      const errors = e?.data?.submitEmploymentForm?.errors?.messages.join(', ');
      toastError(svgs.DELETE_ICON, errors || 'An error occurred during submission.');
    } finally {
      setLoaderState(false);
    }
  };

  const totalQuestions = 9;
  const answeredCount = (values) =>
    [
      values.natureOfEmployment,
      values.durationFrom && values.durationTo,
      values.lastGradeLevel,
      values.deptPositionA,
      values.totalGrossSalary,
      values.reasonForLeaving,
      values.integrityOpinion,
      Object.values(values.ratings || {}).every(Boolean) && 'ratings',
      values.refereeName &&
        values.refereeCompany &&
        values.refereeJobTitle &&
        values.refereeAddress
    ].filter(Boolean).length;

  if (isReadOnly) {
    return (
      <EmploymentReport
        submission={submissionData?.employmentSubmission}
        extra={parsedExtraData}
        employeeName={employeeName}
      />
    );
  }

  return (
    <div style={formStyles.wrapper}>
      <Formik
        enableReinitialize
        initialValues={{
          natureOfEmployment: parsedExtraData.natureOfEmployment || '',
          durationFrom: parsedExtraData.durationFrom || '',
          durationTo: parsedExtraData.durationTo || '',
          lastGradeLevel: parsedExtraData.lastGradeLevel || '',
          deptPositionA: parsedExtraData.deptPositionA || '',
          deptPositionB: parsedExtraData.deptPositionB || '',
          deptPositionC: parsedExtraData.deptPositionC || '',
          totalGrossSalary: parsedExtraData.totalGrossSalary || '',
          nonMonetizedBenefits: parsedExtraData.nonMonetizedBenefits || '',
          reasonForLeaving: parsedExtraData.reasonForLeaving || '',
          reasonForLeavingDetails: parsedExtraData.reasonForLeavingDetails || '',
          integrityOpinion: parsedExtraData.integrityOpinion || '',
          ratings: parsedExtraData.ratings || {
            honesty: '',
            reliability: '',
            teamSpirit: '',
            decisiveness: '',
            effectiveness: '',
            punctuality: ''
          },
          refereeName: submissionData?.employmentSubmission?.fullname || '',
          refereeCompany: parsedExtraData.refereeCompany || '',
          refereeJobTitle: parsedExtraData.refereeJobTitle || '',
          refereeAddress: submissionData?.employmentSubmission?.address || '',
          phone: submissionData?.employmentSubmission?.phone || '',
          email: submissionData?.employmentSubmission?.email || '',
          idCard: null,
          agreement: false
        }}
        validationSchema={employmentVerificationValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ values }) => (
          <div style={formStyles.shell}>
            <Form>
              <div style={formStyles.formCard}>
                <div style={formStyles.headerCard}>
                  <div style={formStyles.brandBar}>
                    <svgs.LOGO style={formStyles.brandLogo} />
                    <span style={formStyles.brandName}>Workforce Group</span>
                  </div>
                  <div style={formStyles.intro}>
                    <p style={formStyles.eyebrow}>Employment Verification Form</p>
                    <p style={formStyles.subtitle}>
                      Please complete the following details regarding{' '}
                      <b>
                        {employeeName}
                        &apos;s
                      </b>{' '}
                      employment history with your company. Your responses are treated
                      with the highest level of confidentiality.
                    </p>
                    <div style={formStyles.progressTrack}>
                      {Array.from({ length: totalQuestions }).map((_, i) => (
                        <div
                          key={i}
                          style={formStyles.progressSegment(i < answeredCount(values))}
                        />
                      ))}
                    </div>
                    <p style={formStyles.progressLabel}>
                      {answeredCount(values)} of {totalQuestions} questions answered
                    </p>
                  </div>
                </div>

                {/* Nature of Employment */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>1</span>
                    <p style={formStyles.question}>
                      What was the nature of employment?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={formStyles.choiceGrid('repeat(3, 1fr)')}>
                    {options.natureOfEmployment.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.natureOfEmployment === opt)}
                      >
                        <Field
                          type="radio"
                          name="natureOfEmployment"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="natureOfEmployment"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* Duration */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>2</span>
                    <p style={formStyles.question}>
                      What was the duration of employment in your company?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div className="row g-3" style={{ width: '90%', margin: '0 auto' }}>
                    <div className="col-md-6">
                      <label
                        style={{ ...formStyles.fieldLabel, width: '100%', margin: 0 }}
                      >
                        From
                      </label>
                      <Field
                        type="date"
                        name="durationFrom"
                        style={{ ...formStyles.input, width: '100%', margin: '8px 0 0' }}
                      />
                      <ErrorMessage
                        name="durationFrom"
                        component="div"
                        style={{
                          ...formStyles.errorText,
                          width: '100%',
                          margin: '8px 0 0'
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label
                        style={{ ...formStyles.fieldLabel, width: '100%', margin: 0 }}
                      >
                        To
                      </label>
                      <Field
                        type="date"
                        name="durationTo"
                        style={{ ...formStyles.input, width: '100%', margin: '8px 0 0' }}
                      />
                      <ErrorMessage
                        name="durationTo"
                        component="div"
                        style={{
                          ...formStyles.errorText,
                          width: '100%',
                          margin: '8px 0 0'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Last Grade Level */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>3</span>
                    <p style={formStyles.question}>
                      What was the candidate&apos;s last grade level?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <Field
                    name="lastGradeLevel"
                    type="text"
                    placeholder="Enter last grade level"
                    style={formStyles.input}
                  />
                  <ErrorMessage
                    name="lastGradeLevel"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* Departments/Positions */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>4</span>
                    <p style={formStyles.question}>
                      What were their last three departments/units and positions held?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={{ width: '90%', margin: '0 auto' }}>
                    <label
                      style={{
                        ...formStyles.fieldLabel,
                        width: '100%',
                        margin: '0 0 8px'
                      }}
                    >
                      Most recent department/unit and position{' '}
                      <span style={formStyles.required}>*</span>
                    </label>
                    <Field
                      name="deptPositionA"
                      type="text"
                      placeholder="e.g. Finance — Senior Accountant"
                      style={{ ...formStyles.input, width: '100%', margin: '0 0 6px' }}
                    />
                    <ErrorMessage
                      name="deptPositionA"
                      component="div"
                      style={{
                        ...formStyles.errorText,
                        width: '100%',
                        margin: '0 0 18px'
                      }}
                    />

                    <label
                      style={{
                        ...formStyles.fieldLabel,
                        width: '100%',
                        margin: '0 0 8px'
                      }}
                    >
                      Second most recent (optional)
                    </label>
                    <Field
                      name="deptPositionB"
                      type="text"
                      placeholder="e.g. Operations — Team Lead"
                      style={{ ...formStyles.input, width: '100%', margin: '0 0 18px' }}
                    />

                    <label
                      style={{
                        ...formStyles.fieldLabel,
                        width: '100%',
                        margin: '0 0 8px'
                      }}
                    >
                      Third most recent (optional)
                    </label>
                    <Field
                      name="deptPositionC"
                      type="text"
                      placeholder="e.g. Customer Service — Associate"
                      style={{ ...formStyles.input, width: '100%', margin: 0 }}
                    />
                  </div>
                </div>

                {/* Salary & Benefits */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>5</span>
                    <p style={formStyles.question}>
                      What was their total gross salary (per annum) and any non-monetized
                      benefits? <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <Field
                    name="totalGrossSalary"
                    type="text"
                    placeholder="e.g. 5,000,000"
                    style={formStyles.input}
                  />
                  <ErrorMessage
                    name="totalGrossSalary"
                    component="div"
                    style={formStyles.errorText}
                  />
                  <label style={formStyles.commentLabel}>
                    Non-monetized benefits (optional)
                  </label>
                  <Field
                    name="nonMonetizedBenefits"
                    as="textarea"
                    rows="2"
                    placeholder="Specify non-monetized benefits..."
                    style={formStyles.input}
                  />
                </div>

                {/* Reason for Leaving */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>6</span>
                    <p style={formStyles.question}>
                      What was their reason for leaving?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={formStyles.choiceGrid('repeat(3, 1fr)')}>
                    {options.reasonForLeaving.map((opt) => (
                      <label
                        key={opt}
                        style={formStyles.choice(values.reasonForLeaving === opt)}
                      >
                        <Field
                          type="radio"
                          name="reasonForLeaving"
                          value={opt}
                          className="d-none"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <ErrorMessage
                    name="reasonForLeaving"
                    component="div"
                    style={formStyles.errorText}
                  />
                  <label style={formStyles.commentLabel}>
                    Please give brief details, if any (optional)
                  </label>
                  <Field
                    name="reasonForLeavingDetails"
                    as="textarea"
                    rows="3"
                    placeholder="Enter details..."
                    style={formStyles.input}
                  />
                </div>

                {/* Integrity Opinion */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>7</span>
                    <p style={formStyles.question}>
                      In your opinion, is the candidate a person of integrity?{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <Field
                    name="integrityOpinion"
                    as="textarea"
                    rows="3"
                    placeholder="Please state your opinion details..."
                    style={formStyles.input}
                  />
                  <ErrorMessage
                    name="integrityOpinion"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>

                {/* Ratings Matrix */}
                <div style={formStyles.card}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>8</span>
                    <p style={formStyles.question}>
                      Based on your knowledge of and interaction with the candidate,
                      please rate them on the following{' '}
                      <span style={formStyles.required}>*</span>
                    </p>
                  </div>
                  <div style={formStyles.matrixWrapper}>
                    <table style={formStyles.matrixTable}>
                      <thead>
                        <tr>
                          <th style={{ ...formStyles.matrixHeader, textAlign: 'left' }}>
                            Parameter
                          </th>
                          {ratingScale.map((scale) => (
                            <th key={scale} style={formStyles.matrixHeader}>
                              {scale}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ratingParameters.map((param) => (
                          <tr key={param.id}>
                            <td style={{ ...formStyles.matrixCell, textAlign: 'left' }}>
                              {param.label}
                              <ErrorMessage
                                name={`ratings.${param.id}`}
                                component="div"
                                style={{
                                  ...formStyles.errorText,
                                  width: '100%',
                                  margin: '4px 0 0'
                                }}
                              />
                            </td>
                            {ratingScale.map((scale) => (
                              <td key={scale} style={formStyles.matrixCell}>
                                <label
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '8px 0',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Field
                                    type="radio"
                                    name={`ratings.${param.id}`}
                                    value={scale}
                                    style={{ width: '18px', height: '18px' }}
                                  />
                                </label>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Verifying Officer Details */}
                <div style={formStyles.cardLast}>
                  <div style={formStyles.cardHead}>
                    <span style={formStyles.cardIndex}>9</span>
                    <div>
                      <p style={formStyles.question}>
                        Please share your details as the Verifying Officer{' '}
                        <span style={formStyles.required}>*</span>
                      </p>
                      <p style={{ ...formStyles.helperText, color: form.slate }}>
                        This confirms who provided this employment reference and how we
                        can reach you if we need to follow up.
                      </p>
                    </div>
                  </div>

                  <div style={{ width: '90%', margin: '0 auto' }}>
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <label
                          style={{ ...formStyles.fieldLabel, width: '100%', margin: 0 }}
                        >
                          Full Name <span style={formStyles.required}>*</span>
                        </label>
                        <Field
                          name="refereeName"
                          type="text"
                          placeholder="Verifying officer's name"
                          style={{
                            ...formStyles.input,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                        <ErrorMessage
                          name="refereeName"
                          component="div"
                          style={{
                            ...formStyles.errorText,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label
                          style={{ ...formStyles.fieldLabel, width: '100%', margin: 0 }}
                        >
                          Company / Organisation Name{' '}
                          <span style={formStyles.required}>*</span>
                        </label>
                        <Field
                          name="refereeCompany"
                          type="text"
                          placeholder="Company / organisation name"
                          style={{
                            ...formStyles.input,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                        <ErrorMessage
                          name="refereeCompany"
                          component="div"
                          style={{
                            ...formStyles.errorText,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                      </div>
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-12">
                        <label
                          style={{ ...formStyles.fieldLabel, width: '100%', margin: 0 }}
                        >
                          Job Title / Designation{' '}
                          <span style={formStyles.required}>*</span>
                        </label>
                        <Field
                          name="refereeJobTitle"
                          type="text"
                          placeholder="e.g., HR Manager, Compliance Officer"
                          style={{
                            ...formStyles.input,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                        <ErrorMessage
                          name="refereeJobTitle"
                          component="div"
                          style={{
                            ...formStyles.errorText,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-12">
                        <label
                          style={{ ...formStyles.fieldLabel, width: '100%', margin: 0 }}
                        >
                          Company Address <span style={formStyles.required}>*</span>
                        </label>
                        <Field
                          name="refereeAddress"
                          as="textarea"
                          rows="2"
                          placeholder="Company address"
                          style={{
                            ...formStyles.input,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                        <ErrorMessage
                          name="refereeAddress"
                          component="div"
                          style={{
                            ...formStyles.errorText,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                      </div>
                      <div className="col-md-6" style={{ marginTop: '14px' }}>
                        <label
                          style={{ ...formStyles.fieldLabel, width: '100%', margin: 0 }}
                        >
                          Telephone Number <span style={formStyles.required}>*</span>
                        </label>
                        <Field
                          name="phone"
                          type="tel"
                          placeholder="080..."
                          style={{
                            ...formStyles.input,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                        <p
                          style={{
                            ...formStyles.helperText,
                            color: form.slate,
                            width: '100%'
                          }}
                        >
                          Official company/personal line
                        </p>
                        <ErrorMessage
                          name="phone"
                          component="div"
                          style={{
                            ...formStyles.errorText,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                      </div>
                      <div className="col-md-6" style={{ marginTop: '14px' }}>
                        <label
                          style={{ ...formStyles.fieldLabel, width: '100%', margin: 0 }}
                        >
                          Email Address <span style={formStyles.required}>*</span>
                        </label>
                        <Field
                          name="email"
                          type="email"
                          placeholder="you@example.com"
                          style={{
                            ...formStyles.input,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                        <p
                          style={{
                            ...formStyles.helperText,
                            color: form.slate,
                            width: '100%'
                          }}
                        >
                          Preferably company email
                        </p>
                        <ErrorMessage
                          name="email"
                          component="div"
                          style={{
                            ...formStyles.errorText,
                            width: '100%',
                            margin: '8px 0 0'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Agreement & Submit */}
              <div style={formStyles.agreementCard}>
                <Field
                  type="checkbox"
                  name="agreement"
                  id="agreementCheck"
                  style={{
                    width: '20px',
                    height: '20px',
                    flexShrink: 0,
                    marginTop: '3px'
                  }}
                />
                <div>
                  <label
                    htmlFor="agreementCheck"
                    style={{
                      cursor: 'pointer',
                      lineHeight: '1.5',
                      color: form.ink,
                      fontWeight: 600,
                      fontSize: '1.05rem'
                    }}
                  >
                    By checking this box <span style={formStyles.required}>*</span>, I
                    certify that I am authorised to provide this information on behalf of{' '}
                    {values.refereeCompany || 'the company named above'}, and that all
                    information provided regarding the employment record of {employeeName}{' '}
                    is accurate and true.
                  </label>
                  <ErrorMessage
                    name="agreement"
                    component="div"
                    style={formStyles.errorText}
                  />
                </div>
              </div>

              <div style={formStyles.submitBar}>
                <button type="submit" style={formStyles.submitButton}>
                  {showLoader ? <Loader /> : 'Submit Verification Form'}
                </button>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default EmploymentVerificationForm;
